import dynamic from 'next/dynamic';
import { lazily } from 'react-lazily';
import { useRouter } from 'next/router';
import { isPossiblePhoneNumber, Value } from 'react-phone-number-input';
import { useForm, FormProvider, Controller, useWatch } from 'react-hook-form';
import { useState, Suspense, useMemo, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { CountryCode } from 'libphonenumber-js';

import {
    Country,
    apiErrorHandler,
    getYearsOptions,
    validatePostcode,
    postcodeSampleMap,
    hasNestedProperty,
    getLocaleMonthOptions,
    getCountriesNormalize,
    getDayOptionsForMonthYear,
} from '@login/helpers';

import { calculateAgeByDate } from 'helpers/date';

import {
    postFullRegister,
    PostFullRegisterErrors,
    PostFullRegisterRequest,
    PostErrorFullRegisterResponse,
} from 'services/idm';

import { FormHeader } from '@login/components/form-header';
import { ButtonsToggler } from '@login/components/buttons-toggler';
import { ApiErrorBlock } from '@login/components/api-error-block';
import { BirthPlaceBlock } from '@login/components/birth-place-block';

import styles from './fullregister.module.scss';
import { FormBox } from '@login/components/form-box';
import { useScrollTo } from 'hooks/use-scroll-to';
import { useAppTranslation } from 'hooks/use-translates';
import { useCaseInsensitiveParam } from 'hooks/use-case-insensitive-params';
import { EMAIL_PATTERN, PASSWORD_PATTERN } from '@login/lib/constants';

const Button = dynamic(() => import('core/generic/elements/button'), {
    ssr: false,
});
const Loading = dynamic(() => import('core/generic/elements/loading'), {
    ssr: false,
});
const InfoBlock = dynamic(() => import('core/generic/elements/info-block'), {
    ssr: false,
});
const { Insert, Select, DropdownList, Phone, Hint } = lazily(
    () => import('core/generic/elements/form')
);
const { Divider } = lazily(() => import('core/generic/elements/divider'));

type FullRegisterFormUrls = {
    registerUrl?: string;
    signInUrl?: string;
    forgottenPasswordUrl?: string;
    privacyPolicyUrl?: string;
    supportName?: string;
    siteName?: string;
    minimumAge?: string;
};

type FullRegisterFormProps = {
    urls: FullRegisterFormUrls | null;
    marketingMessage: string;
    disclaimerMessage: string;
    token?: string | null;
    countries: Country[] | null;
    channelSpecificFields: {
        showCountyOfBirthForSpecifiedCountries: string[];
        showAndRequireCityOfBirth?: boolean | undefined;
        showAndRequireCountryOfBirth?: boolean | undefined;
        showAndRequireCountyOfBirth?: boolean | undefined;
    } | undefined;
};

type FormDataType = {
    dayOfBirth: string;
    monthOfBirth: string;
    yearOfBirth: string;
    email: string;
    password: string;
    passwordConfirm: string;
    firstName: string;
    lastName: string;
    address: {
        countryCode: string;
        city: string;
        addressLine1: string;
        addressLine2?: string;
        addressLine3?: string;
        postcode: string;
        birthCountry?: string;
        birthCounty?: string;
        birthCity?: string;
    };
    mobilePhone: string;
    marketingOptIn: boolean;
};

export const FullRegisterForm = ({
    urls,
    token,
    marketingMessage,
    disclaimerMessage,
    countries,
    channelSpecificFields,
}: FullRegisterFormProps) => {
    const { i18n } = useTranslation();
    const { push } = useRouter();
    const { scrollToSection } = useScrollTo();
    const [apiErrorMessage, setApiErrorMessage] = useState<
        PostFullRegisterErrors | string | null
    >(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const { t } = useAppTranslation(urls);
    const returnUrl = useCaseInsensitiveParam('returnUrl');
    const methods = useForm<PostFullRegisterRequest>({ mode: 'onBlur' })
    const {
        control,
        handleSubmit,
        register,
        getValues,
        setValue,
        setError,
        clearErrors,
        trigger,
        formState: { errors },
    } = methods;

    const selectedMonth = useWatch({ control, name: 'monthOfBirth' });
    const selectedYear = useWatch({ control, name: 'yearOfBirth' });
    const selectedDay = useWatch({ control, name: 'dayOfBirth' });
    const selectedCountry = useWatch({ control, name: 'address.countryCode' });
    const selectedPostcode = useWatch({ control, name: 'address.postcode' });

    const dayOptions = useMemo(
        () => getDayOptionsForMonthYear(selectedMonth, selectedYear),
        [selectedMonth, selectedYear]
    );

    const defaultCountryCode: CountryCode = useMemo(() => {
        if (countries && countries.length && selectedCountry) {
            return countries.filter((value) => value.shortName === selectedCountry)[0].shortName as CountryCode;
        }
        return "IT"
    }, [selectedCountry]);

    const postcodeSample = useMemo(() => postcodeSampleMap[selectedCountry as CountryCode] || '', [selectedCountry]);

    useEffect(() => {
        const maxDay = dayOptions.length;
        if (+selectedDay > maxDay) {
            setValue('dayOfBirth', '1');
        }
        if (selectedDay && selectedMonth && selectedYear && urls?.minimumAge) {
            underMinAgeHandler(
                selectedDay,
                selectedMonth,
                selectedYear,
                urls.minimumAge
            );
        }
    }, [selectedMonth, selectedYear, selectedDay, dayOptions.length, setValue, urls?.minimumAge]);

    useEffect(() => {
        if (selectedPostcode) {
            trigger("address.postcode");
        }
    }, [selectedCountry, trigger]);

    const underMinAgeHandler = (
        day: string,
        month: string,
        year: string,
        minAge: string
    ) => {
        const birthDate = new Date(
            Number(year),
            Number(month) - 1,
            Number(day)
        );
        if (isNaN(birthDate.getTime())) {
            setError('birthDate', { message: 'Invalid birth date' });
            return;
        }

        const age = calculateAgeByDate(birthDate);

        if (age < Number(minAge)) {
            setError('birthDate', {
                message: t('fullregister.too_young_error_full')});
            return;
        } else clearErrors('birthDate');
    };

    const handleOnSubmit = async (formData: FormDataType) => {
        try {
            setApiErrorMessage(null);
            setIsSubmitting(true);
            const response = await postFullRegister(
                {
                    ...formData,
                    returnUrl: returnUrl,
                },
                i18n.language,
                token
            );

            if (response.action === 'Redirect' && response.redirectUrl) {
                if (response.redirectUrl.startsWith('/connect')) {
                    const absoluteUrl = `${window.location.origin}${response.redirectUrl}`;
                    window.location.href = absoluteUrl;
                } else {
                    push(response.redirectUrl);
                }
            } else {
                setIsSubmitting(false);
            }
        } catch (e) {
            const error = e as PostErrorFullRegisterResponse;
            apiErrorHandler(error, i18n.language);
            if (error.title === 'VALIDATION_ERROR' && error.status === 400) {
                setApiErrorMessage(error.detail);
            } else if (
                error.title === 'VALIDATION_ERROR' &&
                error.status === 422
            ) {
                setApiErrorMessage(error.errors);
            } else if (error.title === 'USED_EMAIL_ERROR') {
                setApiErrorMessage({
                    usedEmail: t('fullregister.email_used_error_full'),
                });
            } else if (error.title === 'USED_MOBILE_ERROR') {
                setApiErrorMessage({
                    usedMobile: t('fullregister.mobile_used_error_full'),
                });
            } else {
                setApiErrorMessage({
                    unknownServerError: t('common.500_error_message'),
                });
            }

            setIsSubmitting(false);
        }
    };

    const renderErrorBlock = useCallback(() => {
        let message = '';

        if (typeof apiErrorMessage === "string") {
            message = apiErrorMessage;
        } else if (apiErrorMessage?.usedEmail) {
            message = apiErrorMessage.usedEmail
                .replace("{signInUrl}", urls?.signInUrl || "")
                .replace("{forgottenPasswordUrl}", urls?.forgottenPasswordUrl || "");
        } else if (apiErrorMessage?.usedMobile) {
            message = apiErrorMessage.usedMobile;
        } else if (errors?.birthDate?.message) {
            message = errors.birthDate.message;
        } else if (apiErrorMessage?.unknownServerError) {
            message = apiErrorMessage.unknownServerError;
        }

        return message ? <ApiErrorBlock apiErrorMessage={message} /> : null;
    }, [errors, apiErrorMessage, urls]);

    useEffect(() => {
        const arrayOfKeys: string[] = [
            'birthDate',
            'usedEmail',
            'usedMobile',
            'unknownServerError',
            'dayOfBirth',
            'monthOfBirth',
            'yearOfBirth',
            'email',
            'password',
            'passwordConfirm',
            'firstName',
            'lastName',
            'address.country',
            'address.addressLine1',
            'address.addressLine2',
            'address.addressLine3',
            'address.birthCountry',
            'address.birthCounty',
            'address.birthCity',
            'address.city',
            'address.postcode',
            'mobilePhone',
            'marketingOptIn',
        ];
        let firstErrorElement = null;
        for (const key of arrayOfKeys) {
            if (apiErrorMessage?.hasOwnProperty(key)) {
                firstErrorElement = document.querySelector('[role="alert"]');
                break;
            }
            if (!key.includes('address') && errors?.hasOwnProperty(key)) {
                firstErrorElement = document.getElementById(`root-${key}`);
                break;
            }
            if (key.includes('address') && hasNestedProperty(errors, key)) {
                firstErrorElement = document.getElementById(`root-${key}`);
                break;
            }
            continue;
        }
        if (firstErrorElement && 'scrollIntoView' in firstErrorElement) {
            scrollToSection({ sectionRef: firstErrorElement, offset: 120 });
        }
    }, [errors, apiErrorMessage, scrollToSection]);

    return (
        <Suspense fallback={<Loading />}>
            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(handleOnSubmit)}
                    id="form-profile"
                    noValidate
                >
                    <FormBox>
                        <FormHeader
                            headerTitle={t('common.register_tab')}
                        />
                        <div className={styles['form-box-content']}>
                            {renderErrorBlock()}
                            <InfoBlock variant="info-subtle">
                                <div className={styles['info-block']}>
                                    <span className="body-small">
                                        <span className={styles.redText}>*</span>
                                        {t('common.required_field_text')}
                                    </span>
                                </div>
                            </InfoBlock>
                            <div className={styles.dateSection}>
                                <div>
                                    <div
                                        className={`label-medium ${styles.labelPadding}`}
                                    >
                                        <p>
                                            <span className={styles.redText}>
                                                *
                                            </span>
                                            {t('fullregister.dob_label_full')}
                                        </p>
                                        <Hint text={t('fullregister.dob_help_full')} />
                                    </div>
                                    <div className={styles.selectWrapper}>
                                        <Select
                                            {...register('dayOfBirth', {
                                                required: t(
                                                    'fullregister.day_required_error_full'
                                                ),
                                            })}
                                            name="dayOfBirth"
                                            control={control}
                                            trigger={trigger}
                                            id="dayOfBirth"
                                            label=""
                                            error={
                                                errors.dayOfBirth?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' &&
                                                    apiErrorMessage?.DayOfBirth?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            required={false}
                                            placeholder={t(
                                                'fullregister.day_placeholder_full'
                                            )}
                                            options={dayOptions}
                                        />
                                        <Select
                                            {...register('monthOfBirth', {
                                                required: t(
                                                    'fullregister.month_required_error_full'
                                                ),
                                            })}
                                            name="monthOfBirth"
                                            control={control}
                                            trigger={trigger}
                                            id="monthOfBirth"
                                            placeholder={t(
                                                'fullregister.month_placeholder_full'
                                            )}
                                            label=""
                                            error={
                                                errors.monthOfBirth?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' &&
                                                    apiErrorMessage?.MonthOfBirth?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            required={false}
                                            options={getLocaleMonthOptions(i18n.language)}
                                        />
                                        <Select
                                            {...register('yearOfBirth', {
                                                required: t(
                                                    'fullregister.year_required_error_full'
                                                ),
                                            })}
                                            name="yearOfBirth"
                                            control={control}
                                            trigger={trigger}
                                            id="yearOfBirth"
                                            placeholder={t(
                                                'fullregister.year_placeholder_full'
                                            )}
                                            label=""
                                            error={
                                                errors.yearOfBirth?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' &&
                                                    apiErrorMessage?.YearOfBirth?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            required={false}
                                            options={getYearsOptions()}
                                        />
                                    </div>
                                </div>
                                <div
                                    className={`body-small ${styles.contentSecondaryText}`}
                                >
                                    {t('fullregister.dob_message_full')}
                                </div>
                            </div>
                            {!errors?.birthDate?.message && (
                                <>
                                    <Insert
                                        {...register('email', {
                                            required: t(
                                                'fullregister.email_required_error_full'
                                            ),
                                            pattern: {
                                                value: EMAIL_PATTERN,
                                                message: t(
                                                    'fullregister.email_invalid_error_full'
                                                ),
                                            },
                                        })}
                                        type='email'
                                        defaultValue={''}
                                        id="email"
                                        label={t('fullregister.email_label_full')}
                                        required={true}
                                        error={
                                            errors.email?.message ||
                                            (typeof apiErrorMessage !==
                                                'string' &&
                                                apiErrorMessage?.Email?.join(
                                                    '<br>'
                                                )) ||
                                            ''
                                        }
                                        errorIcon="AlertCircleFilled"
                                        disabled={false}
                                    />
                                    <div className={styles.registerDataSection}>
                                        <Insert
                                            {...register('password', {
                                                validate: (value) => {
                                                    if (!/^[\x00-\x7F]*$/.test(value)) {
                                                        return t('validation.password_english_characters');
                                                    } else if (
                                                        !PASSWORD_PATTERN.test(
                                                            value
                                                        )
                                                    ) {
                                                        return t(
                                                            'fullregister.password_complexity_error_full'
                                                        );
                                                    };
                                                    if (
                                                        value === getValues('email')
                                                    ) {
                                                        return t(
                                                            'fullregister.password_matches_username_error_full'
                                                        );
                                                    }
                                                },
                                            })}
                                            defaultValue={''}
                                            id="password"
                                            label={t('fullregister.password_label_full')}
                                            required={true}
                                            error={
                                                errors.password?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' &&
                                                    apiErrorMessage?.Password?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                            type="password"
                                        />
                                        <Insert
                                            {...register('passwordConfirm', {
                                                required: t(
                                                    'fullregister.password_must_match_error_full'
                                                ),
                                                validate: (value) =>
                                                    value ===
                                                    getValues('password') ||
                                                    t(
                                                        'fullregister.password_must_match_error_full'
                                                    ),
                                            })}
                                            defaultValue={''}
                                            id="passwordConfirm"
                                            label={t(
                                                'fullregister.confirm_password_label_full'
                                            )}
                                            required={true}
                                            error={
                                                errors.passwordConfirm?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' &&
                                                    apiErrorMessage?.PasswordConfirm?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                            type="password"
                                        />
                                    </div>
                                    <Divider />
                                    <div className={styles.registerDataSection}>
                                        <Insert
                                            {...register('firstName', {
                                                required: t('fullregister.first_name_required_error_full'),
                                            })}
                                            defaultValue={''}
                                            id="firstName"
                                            label={t('fullregister.first_name_label_full')}
                                            required={true}
                                            error={
                                                errors.firstName?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' &&
                                                    apiErrorMessage?.FirstName?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                        />
                                        <Insert
                                            {...register('lastName', {
                                                required: t('fullregister.last_name_required_error_full'),
                                            })}
                                            defaultValue={''}
                                            id="lastName"
                                            label={t('fullregister.last_name_label_full')}
                                            required={true}
                                            error={
                                                errors.lastName?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' &&
                                                    apiErrorMessage?.LastName?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                        />
                                    </div>
                                    <div className={styles.registerDataSection}>
                                        <Select
                                            {...register('address.countryCode', {
                                                required: t('fullregister.country_required_error_full'),
                                            })}
                                            name="address.countryCode"
                                            label={t('fullregister.country_label_full')}
                                            control={control}
                                            trigger={trigger}
                                            id="address.countryCode"
                                            placeholder={t('fullregister.country_placeholder_full')}
                                            error={
                                                errors.address?.countryCode?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' && apiErrorMessage &&
                                                    apiErrorMessage['Address.CountryCode']?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            required={true}
                                            options={getCountriesNormalize(countries || [], "shortName", 'IT')}
                                        />
                                        <Insert
                                            {...register('address.addressLine1', {
                                                required: t(
                                                    'fullregister.address_line1_required_error_full'
                                                ),
                                            })}
                                            defaultValue={''}
                                            id="address.addressLine1"
                                            label={t('fullregister.address_line1_label_full')}
                                            required={true}
                                            error={
                                                errors.address?.addressLine1?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' && apiErrorMessage &&
                                                    apiErrorMessage['Address.AddressLine1']?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                        />
                                        <Insert
                                            {...register('address.addressLine2')}
                                            defaultValue={''}
                                            id="address.addressLine2"
                                            label={t('fullregister.address_line2_label_full')}
                                            required={false}
                                            error={
                                                errors.address?.addressLine2?.message ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                            optional={true}
                                        />
                                        <Insert
                                            {...register('address.addressLine3')}
                                            defaultValue={''}
                                            id="address.addressLine3"
                                            label={t('fullregister.address_line3_label_full')}
                                            required={false}
                                            error={
                                                errors.address?.addressLine3?.message ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                            optional={true}
                                        />
                                        <Insert
                                            {...register('address.city', {
                                                required: t('fullregister.city_required_error_full'),
                                            })}
                                            defaultValue={''}
                                            id="address.city"
                                            label={t('fullregister.city_label_full')}
                                            required={true}
                                            error={
                                                errors.address?.city?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' && apiErrorMessage &&
                                                    apiErrorMessage['Address.City']?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                        />
                                        <Insert
                                            {...register('address.postcode', {
                                                required: t('fullregister.postcode_required_error_full'),
                                                validate: (value) => {
                                                    if (!validatePostcode(selectedCountry as CountryCode, value)) {
                                                        return t('fullregister.postcode_invalid_error_full', {
                                                            postcodeSample,
                                                        });
                                                    }
                                                },
                                            })}
                                            defaultValue={''}
                                            id="address.postcode"
                                            label={t('fullregister.postcode_label_full')}
                                            required={true}
                                            error={
                                                errors.address?.postcode?.message ||
                                                (typeof apiErrorMessage !==
                                                    'string' && apiErrorMessage &&
                                                    apiErrorMessage['Address.Postcode']?.join(
                                                        '<br>'
                                                    )) ||
                                                ''
                                            }
                                            errorIcon="AlertCircleFilled"
                                            disabled={false}
                                        />
                                    </div>
                                    <Phone
                                        rules={{
                                            required: t('fullregister.mobile_phone_required_error_full'),
                                            validate: (value: string) => {
                                                if (!isPossiblePhoneNumber(value)) {
                                                    return t('fullregister.mobile_phone_invalid_error_full');
                                                }
                                            }
                                        }}
                                        label={t('fullregister.mobile_phone_label_full')}
                                        id="mobilePhone"
                                        name="mobilePhone"
                                        error={errors.mobilePhone?.message || (typeof apiErrorMessage !==
                                            'string' && apiErrorMessage &&
                                            apiErrorMessage?.MobilePhone?.join(
                                                '<br>'
                                            )) || ''}
                                        errorIcon="AlertCircleFilled"
                                        defaultValue={
                                            '' as Value
                                        }
                                        defaultCountry={defaultCountryCode}
                                        required={true}
                                        control={control}
                                        onChange={() => ''}
                                    />
                                    <Divider />
                                    {(channelSpecificFields?.showAndRequireCityOfBirth || channelSpecificFields?.showAndRequireCountryOfBirth) && (
                                        <>
                                            <div className={styles.registerDataSection}>
                                                <BirthPlaceBlock
                                                    contriesWithCounty={channelSpecificFields.showCountyOfBirthForSpecifiedCountries}
                                                    countries={countries || []}
                                                    errors={errors}
                                                    channelSpecificFields={channelSpecificFields}
                                                />
                                            </div>
                                            <Divider />
                                        </>
                                    )}
                                    <div className={styles.offerSection}>
                                        {marketingMessage && (
                                            <div className="body-small bold">
                                                <span className={styles.redText}>
                                                    *
                                                </span>
                                                {marketingMessage}
                                            </div>
                                        )}
                                        <div
                                            className={`body-small ${styles.contentSecondaryText}`}
                                        >
                                            {t('fullregister.partner_message_full')}
                                        </div>
                                        <div className={styles.buttonsSection}>
                                            <Controller
                                                name="marketingOptIn"
                                                control={control}
                                                rules={{
                                                    validate: (value) =>
                                                        value !== undefined ||
                                                        t(
                                                            'fullregister.opt_in_required_error_full'
                                                        ),
                                                }}
                                                render={({ field }) => (
                                                    <ButtonsToggler
                                                        id="marketingOptIn"
                                                        trueLabel={t(
                                                            'fullregister.yes_label_full'
                                                        )}
                                                        falseLabel={t(
                                                            'fullregister.no_label_full'
                                                        )}
                                                        value={field.value}
                                                        errorMessage={
                                                            errors.marketingOptIn
                                                                ?.message || ''
                                                        }
                                                        errorIcon="AlertCircleFilled"
                                                        onChange={field.onChange}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className={styles.additionalInfo}>
                                            <div
                                                className={`body-xsmall ${styles.contentSecondaryText}`}
                                            >
                                                {t('fullregister.opt_out_message_full')}
                                            </div>
                                            <div className="body-xsmall bold">
                                                {t(
                                                    'fullregister.transactional_email_message_full'
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {disclaimerMessage && (
                                        <div className={styles['info-block-alert']}>
                                            <InfoBlock variant="info" display="stacked">
                                                <span className="body-small">
                                                    <div
                                                        className={
                                                            styles.disclaimer
                                                        }
                                                        dangerouslySetInnerHTML={{
                                                            __html: disclaimerMessage.replace(
                                                                /\[([^\]]+)\]/g,
                                                                `<a href='${urls?.privacyPolicyUrl ||
                                                                ''
                                                                }'>$1</a>`
                                                            ),
                                                        }}
                                                    />
                                                </span>
                                            </InfoBlock>
                                        </div>
                                    )}
                                    <Button
                                        type="submit"
                                        label={t('fullregister.register_button_full')}
                                        isLoading={isSubmitting}
                                        disabled={isSubmitting}
                                        variant="primary"
                                        fullWidth={true}
                                        className={styles.submitButton}
                                        dimension="large"
                                    />
                                    <div
                                        className={`body-small medium ${styles.signinText}`}
                                    >
                                        <Trans
                                            i18nKey={t('common.sigin_link')}
                                            components={[
                                                <Button
                                                    type="button"
                                                    dimension="medium"
                                                    variant="link"
                                                    className={styles['signin-now']}
                                                />,
                                            ]}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </FormBox>
                </form>
            </FormProvider>
        </Suspense>
    );
};

export default FullRegisterForm;
